import type { ActionRecord, PermissionsFlat, SectionRecord } from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"

type SectionKey = keyof PermissionFeatures

// A switch is always bound to where the backend stores its code. A section can
// still show a row or a feature stored elsewhere: Files shows the Documents
// rights mobile and the AI gateway read as `projects.docs`, and Locations shows
// the analytics and exact-location rights stored under Projects and Leads.
export type BoundRow = {
  parentKey: SectionKey
  subKey: string
  actions: ActionRecord
  label?: string
}

export type BoundFeature = {
  parentKey: SectionKey
  subKey: string
  key: string
  value: boolean | undefined
}

// A row whose actions belong to another module: shown read-only, never saved.
export type InheritedRow = {
  label: string
  sourceKey: SectionKey
  parentKey: SectionKey
  subKey: string
  actions: ActionRecord
}

export type SectionLayout = {
  linkedRows: BoundRow[]
  inherited: InheritedRow[]
  hiddenSubKeys: string[]
  hiddenCodes: string[]
  linkedFeatures: BoundFeature[]
  featureOrder?: string[]
}

type Path = [SectionKey, string, string]

type Relocation = {
  rows?: Array<{ from: [SectionKey, string]; label: string }>
  inherited?: Array<{ from: [SectionKey, string]; label: string }>
  features?: Path[]
  featureOrder?: string[]
}

// View All is never part of a bulk switch: it widens what a role sees, so it
// is only ever turned on by its own toggle.
export const VIEW_ALL_CODES: ReadonlySet<string> = new Set([
  "files_view_all",
  "locations_view_all",
  "esign_view_all"
])

const RELOCATIONS: Partial<Record<SectionKey, Relocation>> = {
  files: {
    rows: [{ from: ["projects", "docs"], label: "Files & Documents" }],
    features: [
      ["projects", "features", "document_review"],
      ["projects", "docs", "project_doc_manage_others"]
    ],
    featureOrder: [
      "files_view_all",
      "files_share",
      "files_customer_visibility",
      "files_request_review",
      "document_review",
      "files_restore",
      "files_permanent_delete",
      "project_doc_manage_others"
    ]
  },
  locations: {
    inherited: [
      { from: ["leads", "leads"], label: "Lead Locations" },
      { from: ["projects", "projects"], label: "Project Locations" },
      {
        from: ["subcontractors", "subcontractors"],
        label: "Subcontractor Locations"
      },
      { from: ["calendar", "calendar"], label: "Appointment Locations" }
    ],
    features: [
      ["projects", "features", "global_map_analytics_view"],
      ["projects", "features", "project_view_exact_location"],
      ["leads", "features", "lead_view_exact_location"]
    ],
    featureOrder: [
      "locations_view_all",
      "global_map_analytics_view",
      "project_view_exact_location",
      "lead_view_exact_location",
      "locations_export"
    ]
  },
  esign: {
    features: [
      ["esign", "signatures", "document_send_for_signature"],
      ["esign", "signatures", "document_void"],
      ["esign", "signatures", "document_countersign"],
      ["esign", "signatures", "document_view_activity"]
    ],
    featureOrder: [
      "esign_view_all",
      "document_send_for_signature",
      "esign_resend",
      "document_void",
      "document_countersign",
      "document_view_activity",
      "esign_download_signed",
      "esign_manage_others"
    ]
  }
}

const emptyLayout = (): SectionLayout => ({
  linkedRows: [],
  inherited: [],
  hiddenSubKeys: [],
  hiddenCodes: [],
  linkedFeatures: []
})

const sectionOf = (permissions: PermissionsFlat, key: SectionKey) =>
  permissions[key] as SectionRecord | undefined

/**
 * Where each section's rows and features come from. A relocation applies only
 * when the backend serves the section it moves into, so a payload from before
 * these sections existed renders exactly as it always did.
 */
export const buildSectionLayouts = (
  permissions: PermissionsFlat
): Partial<Record<SectionKey, SectionLayout>> => {
  const layouts: Partial<Record<SectionKey, SectionLayout>> = {}
  const layoutFor = (key: SectionKey) => {
    layouts[key] ??= emptyLayout()
    return layouts[key] as SectionLayout
  }

  ;(Object.entries(RELOCATIONS) as Array<[SectionKey, Relocation]>).forEach(
    ([target, relocation]) => {
      if (!sectionOf(permissions, target)) return
      const layout = layoutFor(target)
      layout.featureOrder = relocation.featureOrder

      const movedCodes = new Set(
        (relocation.features ?? []).map(([, , code]) => code)
      )

      relocation.rows?.forEach(({ from: [parentKey, subKey], label }) => {
        const actions = sectionOf(permissions, parentKey)?.[subKey]
        if (!actions) return
        const shown = Object.fromEntries(
          Object.entries(actions).filter(([code]) => !movedCodes.has(code))
        )
        layout.linkedRows.push({ parentKey, subKey, actions: shown, label })
        layoutFor(parentKey).hiddenSubKeys.push(subKey)
      })

      relocation.inherited?.forEach(({ from: [parentKey, subKey], label }) => {
        const actions = sectionOf(permissions, parentKey)?.[subKey]
        if (!actions) return
        layout.inherited.push({
          label,
          sourceKey: parentKey,
          parentKey,
          subKey,
          actions
        })
      })

      relocation.features?.forEach(([parentKey, subKey, key]) => {
        const actions = sectionOf(permissions, parentKey)?.[subKey]
        if (!actions || !(key in actions)) return
        layout.linkedFeatures.push({
          parentKey,
          subKey,
          key,
          value: actions[key]
        })
        if (parentKey === target && subKey !== "features") {
          layout.hiddenSubKeys.push(subKey)
        } else {
          layoutFor(parentKey).hiddenCodes.push(key)
        }
      })
    }
  )

  return layouts
}

/** Every feature a section shows, in the order the section lists them. */
export const sectionFeatures = (
  parentKey: SectionKey,
  ownFeatures: ActionRecord | undefined,
  layout?: SectionLayout
): BoundFeature[] => {
  const hidden = new Set(layout?.hiddenCodes ?? [])
  const own: BoundFeature[] = Object.entries(ownFeatures ?? {})
    .filter(([key]) => !hidden.has(key))
    .map(([key, value]) => ({ parentKey, subKey: "features", key, value }))
  const all = [...own, ...(layout?.linkedFeatures ?? [])]
  const order = layout?.featureOrder
  if (!order) return all
  const rank = (key: string) => {
    const at = order.indexOf(key)
    return at === -1 ? order.length : at
  }
  return [...all].sort((a, b) => rank(a.key) - rank(b.key))
}

/** Every row a section shows in its grid. */
export const sectionRows = (
  parentKey: SectionKey,
  rest: SectionRecord,
  layout?: SectionLayout
): BoundRow[] => {
  const hiddenRows = new Set(layout?.hiddenSubKeys ?? [])
  const hiddenCodes = new Set(layout?.hiddenCodes ?? [])
  const own: BoundRow[] = Object.entries(rest)
    .filter(([subKey]) => !hiddenRows.has(subKey))
    .map(([subKey, actions]) => ({
      parentKey,
      subKey,
      actions: Object.fromEntries(
        Object.entries(actions).filter(([code]) => !hiddenCodes.has(code))
      )
    }))
  return [...(layout?.linkedRows ?? []), ...own]
}

/** The toggles a section's header counts: its rows and features, not inherited rows. */
export const sectionCounts = (
  parentKey: SectionKey,
  subKeys: SectionRecord,
  layout?: SectionLayout
) => {
  const { features, ...rest } = subKeys
  const values = [
    ...sectionRows(parentKey, rest, layout).flatMap((row) =>
      Object.values(row.actions)
    ),
    ...sectionFeatures(parentKey, features, layout).map(
      (feature) => feature.value
    )
  ]
  return { enabled: values.filter(Boolean).length, total: values.length }
}
